import { useQuery } from "@tanstack/react-query";
import { parseErrorMessage } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import { AuditLogHealthResponse } from "./types";
import { formatNum, formatRelativeTime } from "./helpers";

export function AuditLogHealthPanel() {
  const healthQuery = useQuery<AuditLogHealthResponse>({
    queryKey: ["/api/admin/audit-log-health"],
    queryFn: async () => {
      const res = await fetch("/api/admin/audit-log-health", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load audit-log health");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const data = healthQuery.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold" data-testid="text-audit-log-health-title">
            Audit Log Health
          </h3>
          <p className="text-sm text-muted-foreground">
            Size of the audit log and the status of the nightly retention prune.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => healthQuery.refetch()}
          disabled={healthQuery.isFetching}
          data-testid="button-refresh-audit-log-health"
        >
          {healthQuery.isFetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {healthQuery.isLoading ? (
        <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
      ) : healthQuery.error ? (
        <Card className="p-6">
          <p className="text-sm text-red-600" data-testid="text-audit-log-health-error">
            {parseErrorMessage(healthQuery.error, "Failed to load audit-log health")}
          </p>
        </Card>
      ) : data ? (
        <Card className="p-6 space-y-6" data-testid="card-audit-log-health">
          <div className="flex flex-wrap items-center gap-3">
            {data.overThreshold ? (
              <Badge
                className="bg-red-100 text-red-800 border-red-300 gap-1.5"
                data-testid="badge-audit-log-over-threshold"
              >
                <AlertTriangle className="h-3.5 w-3.5" />
                Abnormally large
              </Badge>
            ) : (
              <Badge
                className="bg-green-100 text-green-800 border-green-300 gap-1.5"
                data-testid="badge-audit-log-healthy"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Healthy
              </Badge>
            )}
            <span className="text-xs text-muted-foreground ml-auto" data-testid="text-audit-log-health-generated-at">
              Updated {new Date(data.generatedAt).toLocaleTimeString()}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-x-8 gap-y-5 sm:grid-cols-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Total rows</div>
              <div className="text-2xl font-semibold tabular-nums" data-testid="text-audit-log-total-rows">
                {formatNum(data.totalRows)}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5" data-testid="text-audit-log-alert-threshold">
                Alert threshold: {formatNum(data.alertRowThreshold)}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Oldest entry</div>
              <div className="text-base font-medium" data-testid="text-audit-log-oldest-entry">
                {data.oldestEntryAt ? new Date(data.oldestEntryAt).toLocaleDateString() : "—"}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {data.oldestEntryAt ? formatRelativeTime(data.oldestEntryAt) : "table is empty"}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Retention window</div>
              <div className="text-base font-medium" data-testid="text-audit-log-retention-months">
                {data.retentionMonths} month{data.retentionMonths === 1 ? "" : "s"}
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Last prune</div>
              <div className="text-base font-medium" data-testid="text-audit-log-last-prune">
                {data.lastPruneAt ? new Date(data.lastPruneAt).toLocaleString() : "Never run"}
              </div>
              {data.lastPruneAt ? (
                <div className="text-xs text-muted-foreground mt-0.5">{formatRelativeTime(data.lastPruneAt)}</div>
              ) : null}
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Rows deleted last prune</div>
              <div className="text-base font-medium tabular-nums" data-testid="text-audit-log-last-prune-deleted">
                {data.lastPruneDeletedCount != null ? formatNum(data.lastPruneDeletedCount) : "—"}
              </div>
              {data.lastPruneRetentionMonths != null ? (
                <div className="text-xs text-muted-foreground mt-0.5">
                  using {data.lastPruneRetentionMonths} month{data.lastPruneRetentionMonths === 1 ? "" : "s"} window
                </div>
              ) : null}
            </div>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
